// @ts-nocheck
/*
 * Port of views/dashboard/api-users-list.js.
 * APIs: API_LIST_OF_API_USERS + API_API_USERS_ACTIVITY_LOGS.
 * SUPERADMIN only. 5-col users table + 5-col activity-logs modal (25 rows).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Activity, RefreshCw } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { API_LIST_OF_API_USERS, API_API_USERS_ACTIVITY_LOGS } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { CalendarDateTimeInput } from '@/components/ui/calendar-inputs';

const METHOD_CLASS = {
  GET: 'bg-emerald-100 text-emerald-700',
  POST: 'bg-sky-100 text-sky-700',
  PUT: 'bg-amber-100 text-amber-700',
  PATCH: 'bg-amber-100 text-amber-700',
  DELETE: 'bg-red-100 text-red-700',
};
const METHOD_OPTIONS = [
  { label: 'All Methods', value: '' },
  { label: 'GET', value: 'GET' },
  { label: 'POST', value: 'POST' },
  { label: 'PUT', value: 'PUT' },
  { label: 'PATCH', value: 'PATCH' },
  { label: 'DELETE', value: 'DELETE' },
];
const ROWS_PER_PAGE = 25;

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
};
const threeMonthsAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  d.setHours(0, 0, 0, 0);
  return d;
};
const toLocalInput = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const fromLocalInput = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

function ActivityLogsModal({ visible, onHide, user }) {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(new Date());
  const [method, setMethod] = useState('');
  const [search, setSearch] = useState('');
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const minFrom = threeMonthsAgo();
  const today = new Date();

  const fetchLogs = useCallback(
    (pageArg = 1) => {
      setLoading(true);
      const params = { page: pageArg, limit: ROWS_PER_PAGE };
      if (fromDate) params.from_date = fromDate.toISOString();
      if (toDate) params.to_date = toDate.toISOString();
      if (method) params.method = method;
      if (search && search.trim()) params.search = search.trim();
      getApiCall(API_API_USERS_ACTIVITY_LOGS, params)
        .then((resp) => {
          setLogs(Array.isArray(resp?.rows) ? resp.rows : []);
          setTotal(resp?.total || 0);
        })
        .catch((err) => toast.error(friendlyApiError(err) || 'Failed to load activity logs'))
        .finally(() => setLoading(false));
    },
    [fromDate, toDate, method, search],
  );

  useEffect(() => {
    if (!visible) return;
    const initialSearch = user?.username || '';
    setFromDate(firstOfMonth());
    setToDate(new Date());
    setMethod('');
    setSearch(initialSearch);
    setPage(1);
    setLoading(true);
    const params = {
      page: 1,
      limit: ROWS_PER_PAGE,
      from_date: firstOfMonth().toISOString(),
      to_date: new Date().toISOString(),
    };
    if (initialSearch) params.search = initialSearch;
    getApiCall(API_API_USERS_ACTIVITY_LOGS, params)
      .then((resp) => {
        setLogs(Array.isArray(resp?.rows) ? resp.rows : []);
        setTotal(resp?.total || 0);
      })
      .catch((err) => toast.error(typeof err === 'string' ? err : 'Failed to load activity logs'))
      .finally(() => setLoading(false));
  }, [visible, user]);

  const applyFilters = () => { setPage(1); fetchLogs(1); };
  const totalPages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));

  const logColumns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: 'created_at',
        header: 'Time',
        cell: ({ row }) => (row.original?.created_at ? new Date(row.original.created_at).toLocaleString() : '-'),
      },
      { id: 'username', header: 'API Username', cell: ({ row }) => row.original?.user?.username || '-' },
      { id: 'name', header: 'Name', cell: ({ row }) => row.original?.user?.name || '-' },
      {
        accessorKey: 'method',
        header: 'Method',
        cell: ({ row }) => {
          const m = String(row.original?.method || '').toUpperCase();
          if (!m) return '-';
          return (
            <span className={'rounded-full px-2 py-0.5 text-[11px] font-bold ' + (METHOD_CLASS[m] || 'bg-muted text-muted-foreground')}>
              {m}
            </span>
          );
        },
      },
      {
        accessorKey: 'url',
        header: 'Request From',
        cell: ({ row }) => <span className="break-all font-mono text-xs">{row.original?.url || '-'}</span>,
      },
    ],
    [],
  );

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onHide()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            API User Activity Logs
          </DialogTitle>
        </DialogHeader>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">From</label>
            <CalendarDateTimeInput
              value={toLocalInput(fromDate)}
              onChange={(e) => setFromDate(fromLocalInput(e))}
              min={toLocalInput(minFrom)}
              max={toLocalInput(toDate || today)}
              className="h-9 min-w-[200px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">To</label>
            <CalendarDateTimeInput
              value={toLocalInput(toDate)}
              onChange={(e) => setToDate(fromLocalInput(e))}
              min={toLocalInput(fromDate || minFrom)}
              max={toLocalInput(today)}
              className="h-9 min-w-[200px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Method</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            >
              {METHOD_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </div>
          <div className="flex flex-1 flex-col gap-1 min-w-[220px]">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Search</label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              placeholder="API username or request…"
              className="h-9"
            />
          </div>
          <Button size="sm" onClick={applyFilters} disabled={loading}>{loading ? 'Loading…' : 'Apply'}</Button>
        </div>

        <DataTable
          tableId="dashboard.api-users.activity-logs"
          columns={logColumns}
          data={logs}
          isLoading={loading}
          emptyTitle="No activity logs"
          emptyDescription="No activity logs found for the current filters."
          pageSize={ROWS_PER_PAGE}
          exportFilename="api-user-activity.csv"
        />

        {total > ROWS_PER_PAGE && (
          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>Server page {page} of {totalPages} · {total} rows</span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => { const p = Math.max(1, page - 1); setPage(p); fetchLogs(p); }}
                className="rounded border border-border px-2 py-1 disabled:opacity-40"
              >Prev</button>
              <button
                disabled={page >= totalPages}
                onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); fetchLogs(p); }}
                className="rounded border border-border px-2 py-1 disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ApiUsersTable() {
  const [apiUsers, setApiUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);
  const [activityVisible, setActivityVisible] = useState(false);
  const [activityUser, setActivityUser] = useState(null);

  useEffect(() => {
    async function checkUserRole() {
      const rawUser = Storage.getItem('user');
      if (!rawUser) return;
      try {
        const user = JSON.parse(rawUser);
        if (user.role) setLoggedInUserRole(user.role);
      } catch (e) {}
    }
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole !== USER_ROLES.SUPERADMIN) return;
    fetchApiUsers();

  }, [loggedInUserRole]);

  const fetchApiUsers = (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    getApiCall(API_LIST_OF_API_USERS)
      .then((resp) => {
        const formatted = resp.map((item, i) => ({
          key: i + 1,
          id: item.id,
          user_id: item.user_id,
          name: item.user?.name || '-',
          username: item.user?.username || '',
          email: item.user?.email || '-',
          created_at: item.created_at,
          updated_at: item.updated_at,
        }));
        setApiUsers(formatted);
      })
      .catch((err) => toast.error(friendlyApiError(err) || 'Failed to load API users'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  const dateText = (v) => (v ? new Date(v).toLocaleString() : '-');

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'user_id', header: 'User ID' },
      { accessorKey: 'name', header: 'Name' },
      {
        accessorKey: 'created_at',
        header: 'Created At',
        cell: ({ row }) => dateText(row.original.created_at),
      },
      {
        accessorKey: 'updated_at',
        header: 'Last Used',
        cell: ({ row }) => dateText(row.original.updated_at),
      },
      {
        id: 'activity',
        header: 'Activity',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              setActivityUser(row.original);
              setActivityVisible(true);
            }}
            className="h-7"
          >
            <Activity className="mr-1.5 h-3.5 w-3.5" />
            Activity
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-foreground">API Users</span>
            {apiUsers.length > 0 && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                {apiUsers.length}
              </span>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => fetchApiUsers(true)} disabled={refreshing}>
            <RefreshCw className={'mr-1.5 h-3.5 w-3.5 ' + (refreshing ? 'animate-spin' : '')} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>

        <DataTable
          tableId="dashboard.api-users"
          columns={columns}
          data={apiUsers}
          isLoading={loading}
          searchPlaceholder="Search API users…"
          emptyTitle="No API users"
          emptyDescription="No API users are configured."
          pageSize={10}
          exportFilename="api-users.csv"
        />

      <ActivityLogsModal
        visible={activityVisible}
        onHide={() => setActivityVisible(false)}
        user={activityUser}
      />
    </>
  );
}
